import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { faWarning } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { Router } from '@angular/router';

import { PromoCodeService } from '../../core/services/data/promo-code.service';
import { OrderService } from '../../core/services/data/order.service';
import { BooksService } from '../../core/services/data/books.service';
import { UserService } from '../../core/services/data/user.service';
import { ModalService } from '../../core/services/modal.service';
import { AnalyticsService } from '../../core/services/analytics.service';
import { PromoStackPurchaseService } from '../../core/services/data/promo-stack-purchase.service';

import { OrderPromoStackFeatureUpdate } from '../../types/local';
import { CartDiscountDetail } from '../../types/cart-discount-detail.interface';
import { PromoStackService } from '../../core/services/data/promo-stack.service';

const DEFAULT_BOOK_COVER = '/assets/img/defaults/default_book_image_400w_simple.jpg';

const PROMO_TYPE_PERCENT = 'percent';
const PROMO_TYPE_AMOUNT = 'amount';
const PROMO_TYPES = [PROMO_TYPE_PERCENT, PROMO_TYPE_AMOUNT];
const AUTHOR_DASHBOARD = '/author-dashboard';

export const VARIANT_CART_ITEM = 'cartItem';
export const VARIANT_CHECKOUT_ITEM = 'checkoutItem';
const VARIANTS = [
	VARIANT_CART_ITEM,
	VARIANT_CHECKOUT_ITEM,
];
@Component({
	selector: 'app-cart-promo-stack',
	templateUrl: './cart-promo-stack.component.html',
	styleUrls: ['./cart-promo-stack.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CartPromoStackComponent implements OnInit, OnDestroy {
	@Input() qualifiesForMembershipUpsell = false;

	@Input() set issues(_issues: {code: string}[]) {
		if (!_issues) {
			this.soldout = false;
			return;
		}
		this.soldout = _issues.some(({ code }) => code === 'SOLDOUT');
	}

	public itemImage;

	public faWarning = faWarning;

	public VARIANT_CART_ITEM = VARIANT_CART_ITEM;

	public VARIANT_CHECKOUT_ITEM = VARIANT_CHECKOUT_ITEM;

	public _variant = VARIANT_CART_ITEM;

	public detailsExpanded = false;

	public paymentAmount;

	public discountDisplay = '';

	public discount = 0;

	public discountText = 'Discount';

	public duration;

	public audience;

	public errorText;

	public promoStack;

	public pubDate;

	public bookPrice;

	public bookImage;

	public confirmText = 'Are you sure you want to remove this promotion from your cart?';

	public buttonText = { confirm: 'Remove Promotion', cancel: 'Go Back' };

	public loading = false;

	public discountDetails: CartDiscountDetail[] = [];

	public hasSpeculativeDiscount;

	public speculativeDiscountText;

	public upsellText;

	public savingDiscount;

	public soldout = false;

	private author;

	private author$;

	@Input()
	set item(item) {
		if (item && item.item) {
			this.promoStack = item.item;
			this.hasSpeculativeDiscount = item.hasSpeculativeDiscount;
			this.speculativeDiscountText = item.speculativeDiscountText;
		}
	}

	@Input()
	set variant(variant) {
		if (variant && VARIANTS.includes(variant)) {
			this._variant = variant;
		}
	}

	get showMembershipSaving() {
		return this.discount < this.savingDiscount;
	}

	constructor(
		private router: Router,
		private os: OrderService,
		private bs: BooksService,
		private us: UserService,
		public ms: ModalService,
		private pcs: PromoCodeService,
		private analyticsService: AnalyticsService,
		private promoStackPurchaseService: PromoStackPurchaseService,
		private promoStackService: PromoStackService,
	) { }

	ngOnInit() {
		this.author$ = this.us.author$.subscribe(
			(data: any) => {
				this.author = data;
			},
		);

		if (this.promoStack?._promoCode?.code?.includes('MEM-')) {
			this.discountText = 'Member Discount';
		}

		// TODO: Apply Discount
		this.audience = this.promoStack._promoStack.audience;
		this.duration = this.promoStack._promoStack.releaseDuration;
		this.pubDate = moment(this.promoStack.startDate, 'YYYYMMDD').format('MMMM Do, YYYY');
		this.paymentAmount = this.promoStack.paymentAmount;
		this.discount = this.promoStack._promoStack.discountedPrice - this.paymentAmount;
		this.itemImage = this.promoStack._book?.amazonImageUrl || DEFAULT_BOOK_COVER;

		this.generateDiscountDetails();
		this.generateUpsellText();
	}

	ngOnDestroy() {
		this.author$?.unsubscribe();
	}

	get _id() {
		return this.promoStack?._id;
	}

	get modalId() {
		return `remove-confirm-modal-${this._id}`;
	}

	generateDiscountDetails() {
		if (this.hasSpeculativeDiscount) {
			this.discountDetails.push({ label: 'Promo Subtotal Price', value: this.promoStack._promoStack.discountedPrice });
			this.discountDetails.push({ label: 'Membership Discount', value: -this.discount });
			return;
		}

		if (this.hasValidPromoCode() === false) return;

		this.discountDetails.push({ label: 'Promo Price', value: this.promoStack._promoStack.discountedPrice });
		this.discountDetails.push({ label: this.discountText, value: -this.discount });
	}

	generateUpsellText() {
		const perspectiveDiscountAmount = this.promoStack._promoStack.discountedPrice * 0.1;
		const _discount = perspectiveDiscountAmount % 1 ? perspectiveDiscountAmount.toFixed(2) : perspectiveDiscountAmount;
		this.savingDiscount = _discount;

		this.upsellText = `Save $${_discount} by becoming a gold member`;
	}

	hasValidPromoCode() {
		const discountType = this.promoStack?._promoCode?.discountType;
		return discountType && PROMO_TYPES.includes(discountType);
	}

	openConfirmationModal() {
		this.ms.open(this.modalId);
	}

	onItemRemove(event) {
		if (!event) return;

		this.loading = true;
		this.os.removeItem(this._id).subscribe({
			next: () => {
				this.loading = false;
				this.os.handlePromoStackRemoveAnalytics(this.promoStack);
			},
			error: (error) => {
				this.loading = false;
				this.errorText = [`Uh oh, something went wrong. ${error}`];
				this.ms.open('cart-item-error-modal');
			},
		});
		this.ms.close(this.modalId);
	}

	toggleDetails() {
		this.detailsExpanded = !this.detailsExpanded;
	}

	editStack() {
		this.promoStackPurchaseService.editPromoStack(this.promoStack);
		const link = this.promoStackPurchaseService.getLink();

		const authorPath = this.author ? `${AUTHOR_DASHBOARD}/${this.author.authorLink}` : '';

		this.os.setCartNotVisible();

		this.router.navigate([authorPath + link]);
	}

	changeDate() {
		this.promoStackPurchaseService.getAvailability(this.promoStack._promoStack._id);
		this.ms.open('promo-stack-date-picker');
	}

	confirmDate() {
		const updates: OrderPromoStackFeatureUpdate = {
			id: this.promoStack._id,
			dateSet: this.promoStackPurchaseService.selectedDateSet,
		};

		this.promoStackService.updatePromoStackDates(updates)
			.subscribe({
				next: (result) => {
					if (result.success) {
						this.os.refreshCart();
						this.ms.close('promo-stack-date-picker');
					}
				},
			});
	}
}
